import axios, { AxiosPromise } from "axios";
import { I18nLocale } from "@/i18nConfig.server";
import { OrganizationListAPI } from "@/apiTypes/company-list-api.type";
import {
  createUrlWithApiClient,
  mediaPopulationFields,
} from "@/cms-helpers/url.helper.client";
import { cache } from "react";
import { InitiativeClassificationAPI } from "@/apiTypes/initiative-classification-api.type";
import {
  allNewsApiPath,
  globalSearchApiPath,
  initiativeClassificationApiPath,
  organizationApiPath,
} from "@/cms-helpers/api-constant.client";
import { AllNewsAPI } from "@/apiTypes/news-listing-api.type";
import { GlobalSearchAPI } from "@/apiTypes/global-search-api.type";

export const handleAxiosPromise = cache(async <T>(promise: AxiosPromise<T>) => {
  const maxRetries = 4;
  let retries = 0;
  const retryDelay = 400;
  const retry = async () => {
    retries++;
    return new Promise<T>((resolve, reject) => {
      setTimeout(
        () => {
          promise
            .then((res) => {
              resolve(res.data);
            })
            .catch(() => {
              if (retries >= maxRetries) {
                return reject(
                  `Error in fetching data after ${retries} retries`,
                );
              } else {
                retry();
              }
            });
        },
        retries === 0 ? 0 : retryDelay,
        // retryDelay,
      );
    });
  };
  return retry();
});

export const getOrganizationListForForm = cache(
  async (locale: I18nLocale = "en") => {
    const url = createUrlWithApiClient({
      path: organizationApiPath,
      query: {
        locale,
        fields: ["title", "slug", "locale", "organizationType"],
        sort: "position:asc",
      },
    });
    return axios<OrganizationListAPI>(url)
      .then(({ data: { data } }) => {
        return data;
      })
      .catch(() => {
        throw new Error("Error in fetching company list data");
      });
  },
);

export const getInitiativeClassificationList = cache(
  async (locale: I18nLocale = "en", company?: string) => {
    if (!company) return;
    const url = createUrlWithApiClient({
      path: initiativeClassificationApiPath,
      query: {
        locale,
        fields: ["title", "slug", "locale"],
        filters: {
          slug: {
            $nei: "all-initiatives",
          },
          initiatives: {
            company: {
              slug: {
                $containsi: company,
              },
            },
          },
        },
        sort: "position:asc",
      },
    });
    return axios<InitiativeClassificationAPI>(url)
      .then(({ data: { data } }) => {
        return data;
      })
      .catch(() => {
        throw new Error("Error in fetching initiatives classification data");
      });
  },
);

export const globalSearch = cache(
  async (keyword: string, locale: I18nLocale = "en") => {
    if (!keyword) return;
    const url = createUrlWithApiClient({
      path: globalSearchApiPath,
      query: {
        locale,
        search: keyword,
      },
    });
    return axios<GlobalSearchAPI>(url)
      .then((res) => {
        return res.data;
      })
      .catch(() => {
        throw new Error("Error in fetching global search data");
      });
  },
);

export interface AllNewsFilterOptions {
  keyword?: string;
  organization?: string;
  topic?: string;
  year?: string;
  sort?: "asc" | "desc";
}

export const getAllNewsData = cache(
  async (locale: I18nLocale = "en", options: AllNewsFilterOptions) => {
    const {
      keyword = "",
      organization = "",
      topic = "",
      year = "",
      sort = "desc",
    } = options;
    const filterQuery = {
      $notNull: true,
    };

    const url = createUrlWithApiClient({
      path: allNewsApiPath,
      query: {
        locale,
        fields: [
          "Title",
          "slug",
          "newsDate",
          "newsType",
          "minRead",
          "associateBy",
          "shortContent",
        ],
        populate: {
          image: {
            fields: mediaPopulationFields,
          },
          associatedCompany: {
            fields: ["title"],
          },
        },
        filters: {
          $or: [
            {
              shortContent: {
                $containsi: keyword,
              },
            },
            {
              newsDate: {
                $containsi: keyword,
              },
            },
            {
              newsType: {
                $containsi: keyword,
              },
            },
            {
              minRead: {
                $containsi: keyword,
              },
            },
            {
              Title: {
                $containsi: keyword,
              },
            },
            {
              slug: {
                $containsi: keyword,
              },
            },
            {
              associateBy: {
                $containsi: keyword,
              },
            },
            {
              associatedCompany: {
                title: {
                  $containsi: keyword,
                },
              },
            },
          ],
          shortContent: filterQuery,
          newsDate: year
            ? {
                $gte: `${year}-01-01`,
                $lte: `${year}-12-31`,
              }
            : filterQuery,
          newsType: topic
            ? {
                $eqi: topic,
              }
            : filterQuery,
          minRead: filterQuery,
          Title: filterQuery,
          slug: filterQuery,
          associateBy: filterQuery,
          associatedCompany: {
            slug: filterQuery,
            title: organization
              ? {
                  $eqi: organization,
                }
              : filterQuery,
          },
        },
        sort: `createdAt:${sort}`,
      },
    });
    return axios<AllNewsAPI>(url)
      .then((res) => {
        return res.data;
      })
      .catch(() => {
        throw new Error("Error in fetching all news data");
      });
  },
);
